import React from 'react';
import { 
  Globe, 
  Users, 
  Car, 
  Building, 
  Shield, 
  Star, 
  MapPin, 
  CreditCard, 
  MessageSquare,
  CheckCircle,
  Zap,
  TrendingUp,
  Search
} from 'lucide-react';
import { isMobile, isTablet } from '../../utils/deviceUtils';

export const About: React.FC = () => {
  const mobile = isMobile();
  const tablet = isTablet();

  const platformFeatures = [
    {
      icon: Globe,
      title: 'Safari Discovery',
      description: 'Browse curated safari packages from verified tour operators across Tanzania',
      color: 'primary',
    },
    {
      icon: Car,
      title: 'Transport Booking',
      description: 'Book reliable transportation services from licensed transport operators',
      color: 'accent-coral',
    },
    {
      icon: Building,
      title: 'Operator Platform',
      description: 'Tour operators can list safaris, manage bookings, and grow their business',
      color: 'accent-success',
    },
    {
      icon: Users,
      title: 'Direct Connection',
      description: 'Connect travelers directly with operators, eliminating middlemen fees',
      color: 'accent-warning',
    },
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Browse & Discover',
      description: 'Explore safari packages by destination, date, and price. Filter by operator ratings and special requirements.',
      icon: Search,
    },
    {
      step: '02',
      title: 'Book Securely',
      description: 'Make instant bookings with secure payment processing. Receive immediate confirmation and booking details.',
      icon: CreditCard,
    },
    {
      step: '03',
      title: 'Connect & Travel',
      description: 'Direct communication with operators, real-time updates, and 24/7 support throughout your journey.',
      icon: MessageSquare,
    },
  ];

  const platformStats = [
    { value: '500+', label: 'Tour Operators', icon: Building },
    { value: '200+', label: 'Transport Providers', icon: Car },
    { value: '50+', label: 'Destinations', icon: MapPin },
    { value: '15K+', label: 'Happy Travelers', icon: Users },
  ];

  const keyBenefits = [
    {
      icon: Shield,
      title: 'Verified Operators',
      description: 'All operators are licensed, insured, and verified for your safety',
    },
    {
      icon: Star,
      title: 'Real Reviews',
      description: 'Authentic reviews from travelers who experienced the safaris',
    },
    {
      icon: Zap,
      title: 'Instant Booking',
      description: 'Book immediately with real-time availability and instant confirmation',
    },
    {
      icon: TrendingUp,
      title: 'Best Prices',
      description: 'Direct booking eliminates commission fees, saving you money',
    },
  ];

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Hero Section */}
      <section className="relative h-[80vh] overflow-hidden">
        {/* Solid color header for navbar visibility */}
        <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-black/80 to-transparent z-10"></div>
        
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/4577801/pexels-photo-4577801.jpeg"
            alt="Safari landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/30"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center text-center">
          <div className="max-w-6xl px-4 w-full p-4 sm:p-6 lg:p-8">
            <h1 className={`${mobile ? 'text-4xl' : tablet ? 'text-5xl' : 'text-6xl'} font-black text-white mb-6 leading-tight`}>
              The Safari Booking
              <span className="block text-transparent bg-gradient-to-r from-accent-coral-400 via-accent-teal-400 to-primary-400 bg-clip-text animate-pulse mt-2">
                Platform
              </span>
            </h1>
            <p className={`${mobile ? 'text-lg' : 'text-xl'} text-white/90 max-w-3xl mx-auto leading-relaxed mb-8`}>
              Connecting travelers with authentic safari experiences through our innovative 
              booking platform. Browse, book, and connect directly with Tanzania's finest 
              tour operators and transport providers.
            </p>
            
            {/* Floating Elements */}
            <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-accent-coral-400/30 rounded-full animate-bounce"
                 style={{ animationDelay: '0s', animationDuration: '3s' }}></div>
            <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-accent-teal-400/30 rounded-full animate-bounce"
                 style={{ animationDelay: '1s', animationDuration: '4s' }}></div>
            <div className="absolute bottom-1/4 left-1/3 w-2 h-2 bg-primary-400/30 rounded-full animate-bounce"
                 style={{ animationDelay: '2s', animationDuration: '5s' }}></div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className={`${mobile ? 'py-16' : tablet ? 'py-20' : 'py-24'} bg-background-primary`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-5xl'} font-bold text-text-primary mb-6`}>
              What We Do
            </h2>
            <p className={`${mobile ? 'text-lg' : 'text-xl'} text-text-secondary max-w-4xl mx-auto leading-relaxed`}>
              We're not just another booking site. We're a comprehensive platform that connects 
              travelers with verified safari operators and transport providers across Tanzania.
          </p>
        </div>

          {/* Platform Features Grid */}
          <div className={`grid ${mobile ? 'grid-cols-1 gap-6' : tablet ? 'grid-cols-2 gap-8' : 'grid-cols-2 lg:grid-cols-4 gap-8'} mb-16`}>
            {platformFeatures.map((feature, index) => {
              const Icon = feature.icon;
            return (
              <div
                key={index}
                  className="bg-white p-6 rounded-xl shadow-soft hover:shadow-large transition-all duration-300 text-center group"
                >
                  <div className={`w-12 h-12 bg-${feature.color}-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-6 h-6 text-${feature.color}-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-text-primary mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {feature.description}
                  </p>
              </div>
            );
          })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className={`${mobile ? 'py-16' : tablet ? 'py-20' : 'py-24'} bg-background-secondary`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-5xl'} font-bold text-text-primary mb-6`}>
              How It Works
            </h2>
            <p className={`${mobile ? 'text-lg' : 'text-xl'} text-text-secondary max-w-3xl mx-auto leading-relaxed`}>
              Simple, secure, and seamless - from discovery to departure
            </p>
          </div>

          <div className={`grid ${mobile ? 'grid-cols-1 gap-8' : tablet ? 'grid-cols-1 md:grid-cols-3 gap-8' : 'grid-cols-1 md:grid-cols-3 gap-12'}`}>
            {howItWorks.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative">
                  <div className="bg-white p-6 rounded-xl shadow-soft text-center">
                    <div className="w-12 h-12 bg-primary-500 text-white rounded-xl flex items-center justify-center mx-auto mb-4 font-black text-lg">
                      {step.step}
                    </div>
                    <Icon className="w-8 h-8 text-primary-500 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-text-primary mb-3">
                      {step.title}
                    </h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      {step.description}
                    </p>
        </div>

                  {/* Connector Line */}
                  {index < howItWorks.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-6 w-12 h-0.5 bg-border-primary transform -translate-y-1/2"></div>
                  )}
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Stats */}
      <section className={`${mobile ? 'py-16' : tablet ? 'py-20' : 'py-24'} bg-gradient-to-r from-primary-500 to-primary-600`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-5xl'} font-bold text-white mb-6`}>
              Platform Impact
            </h2>
            <p className={`${mobile ? 'text-lg' : 'text-xl'} text-white/90 max-w-3xl mx-auto leading-relaxed`}>
              Growing the safari tourism ecosystem in Tanzania
            </p>
          </div>

          <div className={`grid ${mobile ? 'grid-cols-2 gap-6' : tablet ? 'grid-cols-2 md:grid-cols-4 gap-8' : 'grid-cols-2 md:grid-cols-4 gap-12'} text-center`}>
            {platformStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-white">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-3xl font-black mb-2">
                    {stat.value}
                  </div>
                  <div className="text-white/90 text-sm font-medium">
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className={`${mobile ? 'py-16' : tablet ? 'py-20' : 'py-24'} bg-background-primary`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-5xl'} font-bold text-text-primary mb-6`}>
              Why Choose Our Platform
            </h2>
            <p className={`${mobile ? 'text-lg' : 'text-xl'} text-text-secondary max-w-3xl mx-auto leading-relaxed`}>
              Built for travelers, designed for operators, trusted by everyone
          </p>
        </div>

          <div className={`grid ${mobile ? 'grid-cols-1 gap-6' : tablet ? 'grid-cols-2 gap-8' : 'grid-cols-2 lg:grid-cols-4 gap-8'}`}>
            {keyBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-xl shadow-soft hover:shadow-large transition-all duration-300 text-center group">
                  <div className="w-12 h-12 bg-accent-success-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-accent-success-500" />
                  </div>
                  <h3 className="text-lg font-bold text-text-primary mb-3">
                    {benefit.title}
            </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* For Operators Section */}
      <section className={`${mobile ? 'py-16' : tablet ? 'py-20' : 'py-24'} bg-background-secondary`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid ${mobile ? 'grid-cols-1 gap-8' : tablet ? 'grid-cols-1 lg:grid-cols-2 gap-12' : 'grid-cols-1 lg:grid-cols-2 gap-16'} items-center`}>
            
            <div className="p-4 sm:p-6 lg:p-8">
              <h2 className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-5xl'} font-bold text-text-primary mb-6`}>
                For Safari Operators
              </h2>
              <p className={`${mobile ? 'text-lg' : 'text-xl'} text-text-secondary mb-8 leading-relaxed`}>
                Grow your safari business with our comprehensive operator platform. 
                List your packages, manage bookings, and connect with travelers worldwide.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-accent-success-500 flex-shrink-0" />
                  <span className="text-text-secondary">Create and manage safari packages</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-accent-success-500 flex-shrink-0" />
                  <span className="text-text-secondary">Handle bookings and payments</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-accent-success-500 flex-shrink-0" />
                  <span className="text-text-secondary">Access analytics and insights</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-accent-success-500 flex-shrink-0" />
                  <span className="text-text-secondary">Direct customer communication</span>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 lg:p-8">
              <div className="bg-white p-6 rounded-xl shadow-soft">
                <div className="flex items-center gap-3 mb-4">
                  <Building className="w-8 h-8 text-primary-500" />
                  <h3 className="text-xl font-bold text-text-primary">Operator Dashboard</h3>
                </div>
                <p className="text-text-secondary text-sm mb-4">
                  Complete control over your safari business with our intuitive dashboard.
                </p>
                <div className="space-y-2 text-sm text-text-secondary">
                  <div className="flex justify-between">
                    <span>Active Packages</span>
                    <span className="font-semibold text-primary-500">25+</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Bookings</span>
                    <span className="font-semibold text-primary-500">1,247</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Revenue This Month</span>
                    <span className="font-semibold text-primary-500">$45,230</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`${mobile ? 'py-16' : tablet ? 'py-20' : 'py-24'} bg-gradient-to-r from-primary-500 to-primary-600`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-5xl'} font-bold text-white mb-6`}>
            Ready to Experience Tanzania?
          </h2>
          <p className={`${mobile ? 'text-lg' : 'text-xl'} text-white/90 mb-8 leading-relaxed`}>
            Join thousands of travelers and operators who trust our platform for authentic safari experiences.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-primary-500 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-colors shadow-soft hover:shadow-large transform hover:scale-105">
              Browse Safaris
            </button>
            <button className="bg-transparent text-white border-2 border-white px-8 py-4 rounded-xl font-bold hover:bg-white hover:text-primary-500 transition-colors">
              Become an Operator
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
